import { useState, useEffect, useCallback } from 'react';
import { formatDuration, formatNumber } from '@stream-buddy/shared';
import type { AnalyticsData } from '@stream-buddy/shared';

interface AnalyticsDashboardProps {
  token: string;
  serverUrl?: string;
}

interface LatencyPercentiles {
  p50: number | null;
  p95: number | null;
  p99: number | null;
}

interface LatencySummary {
  firstAudio: LatencyPercentiles;
  turnDuration: LatencyPercentiles;
  firstAudioSamples: number;
  turnDurationSamples: number;
  typicalReplyDelayMs?: number | null;
  responsivenessHealth?: 'warming_up' | 'good' | 'ok' | 'slow';
  lastUpdatedAt: number | null;
}

// Compact glass stat card used throughout analytics.
function StatCard({ title, value, subtitle, icon, color = 'blue' }: { title: string; value: string | number; subtitle: string; icon: string; color?: 'blue' | 'purple' | 'emerald' | 'amber' | 'rose' }) {
  const colorMap = {
    blue: 'text-blue-200 bg-blue-500/10 border-blue-400/20 shadow-[0_0_28px_rgba(59,130,246,0.16)]',
    purple: 'text-fuchsia-100 bg-fuchsia-500/10 border-fuchsia-400/20 shadow-[0_0_28px_rgba(168,85,247,0.16)]',
    emerald: 'text-emerald-100 bg-emerald-500/10 border-emerald-400/20 shadow-[0_0_28px_rgba(16,185,129,0.16)]',
    amber: 'text-amber-100 bg-amber-500/10 border-amber-400/20 shadow-[0_0_28px_rgba(245,158,11,0.16)]',
    rose: 'text-rose-100 bg-rose-500/10 border-rose-400/20 shadow-[0_0_28px_rgba(244,63,94,0.16)]',
  };

  return (
    <div className="relative overflow-hidden rounded-[22px] border border-white/10 bg-[linear-gradient(145deg,rgba(255,255,255,0.055),rgba(255,255,255,0.018))] p-4 shadow-[0_18px_46px_rgba(5,9,24,0.24)] transition-all hover:-translate-y-0.5 hover:border-white/15 min-h-[132px]">
      <div className={`absolute inset-0 bg-gradient-to-br ${color === 'blue' ? 'from-blue-500/10 via-transparent to-cyan-400/5' : color === 'purple' ? 'from-fuchsia-500/10 via-transparent to-violet-400/5' : color === 'emerald' ? 'from-emerald-500/10 via-transparent to-cyan-400/5' : color === 'amber' ? 'from-amber-500/10 via-transparent to-orange-400/5' : 'from-rose-500/10 via-transparent to-pink-400/5'}`} />
      <div className="relative flex h-full flex-col justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border text-lg ${colorMap[color]}`}>
            {icon}
          </div>
          <div className="min-w-0 flex-1">
            <span className="block text-[9px] font-black uppercase tracking-[0.18em] text-white/40 leading-4">{title}</span>
          </div>
        </div>

        <div>
          <div className="text-3xl font-black tracking-[-0.05em] text-white leading-none">{value}</div>
          <div className="mt-2 text-[10px] font-bold uppercase tracking-[0.16em] text-white/35 leading-4">{subtitle}</div>
        </div>
      </div>
      <div className={`absolute bottom-0 left-6 right-6 h-px bg-gradient-to-r from-transparent ${color === 'blue' ? 'via-blue-400/70' : color === 'purple' ? 'via-fuchsia-400/70' : color === 'emerald' ? 'via-emerald-400/70' : color === 'amber' ? 'via-amber-400/70' : 'via-rose-400/70'} to-transparent opacity-60`} />
    </div>
  );
}

function formatSessionTimestamp(value: string | Date | null): { date: string; time: string } {
  if (!value) {
    return { date: '--', time: 'Unknown' };
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return { date: '--', time: 'Unknown' };
  }

  return {
    date: parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    time: parsed.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
  };
}

export function AnalyticsDashboard({ token, serverUrl = '' }: AnalyticsDashboardProps) {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [latency, setLatency] = useState<LatencySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [inlineNotice, setInlineNotice] = useState<string | null>(null);
  const [showAdvancedLatency, setShowAdvancedLatency] = useState(false);

  const sleep = useCallback(async (ms: number) => {
    await new Promise(resolve => setTimeout(resolve, ms));
  }, []);

  const fetchAnalytics = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      setInlineNotice(null);
      const headers = {
        'Authorization': `Bearer ${token}`,
      };

      const fetchDashboard = async () => {
        const maxAttempts = 3;
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          const response = await fetch(`${serverUrl}/api/analytics/dashboard?range=${timeRange}`, { headers });

          if (response.ok) {
            return response;
          }

          if (response.status === 401) {
            throw new Error('Session expired. Please refresh and sign in again.');
          }

          const shouldRetry = response.status === 429 || response.status >= 500;
          if (!shouldRetry || attempt === maxAttempts) {
            if (response.status === 429) {
              throw new Error('Too many analytics requests. Please wait a moment and try again.');
            }
            throw new Error('Analytics is temporarily unavailable. Please try again shortly.');
          }

          const retryAfterHeader = response.headers.get('retry-after');
          const retryAfterSeconds = retryAfterHeader ? Number(retryAfterHeader) : NaN;
          const waitMs = Number.isFinite(retryAfterSeconds)
            ? Math.max(250, retryAfterSeconds * 1000)
            : 300 * attempt;
          setInlineNotice('Analytics is catching up. Retrying...');
          await sleep(waitMs);
        }

        throw new Error('Failed to fetch analytics');
      };

      const [dashboardResult, latencyResult] = await Promise.allSettled([
        fetchDashboard(),
        fetch(`${serverUrl}/api/analytics/latency`, { headers }),
      ]);

      if (dashboardResult.status !== 'fulfilled') {
        throw dashboardResult.reason;
      }

      const dashboardPayload = (await dashboardResult.value.json()) as AnalyticsData;
      setData(dashboardPayload);
      setInlineNotice(null);

      if (latencyResult.status === 'fulfilled' && latencyResult.value.ok) {
        const latencyPayload = (await latencyResult.value.json()) as { data?: LatencySummary | null };
        setLatency(latencyPayload.data ?? null);
      } else {
        setLatency(null);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      if (msg.toLowerCase().includes('session expired')) {
        setErrorMessage(msg);
        setShowErrorModal(true);
      } else {
        setInlineNotice(msg);
      }
    } finally {
      setLoading(false);
    }
  }, [token, timeRange, serverUrl, sleep]);

  useEffect(() => {
    void fetchAnalytics();
  }, [fetchAnalytics]);

  const handleClearAnalytics = useCallback(async () => {
    setShowClearConfirm(false);

    try {
      const csrfToken = document.cookie.match(/csrf_token=([^;]+)/)?.[1];
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${token}`,
      };

      if (csrfToken) {
        headers['x-csrf-token'] = csrfToken;
      }

      const response = await fetch(`${serverUrl}/api/analytics`, {
        method: 'DELETE',
        credentials: 'include',
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to clear analytics');
      }

      await fetchAnalytics();
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to clear analytics');
      setShowErrorModal(true);
    }
  }, [token, serverUrl, fetchAnalytics]);

  if (loading) {
    return (
      <div className="h-full min-h-[400px] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full border-t-2 border-l-2 border-blue-500 animate-spin" />
          <div className="text-gray-400 font-black text-xs uppercase tracking-widest animate-pulse">Calculating Stats...</div>
        </div>
      </div>
    );
  }

  const hasUsefulLatency = (latency?.firstAudioSamples ?? 0) >= 20;
  const responsivenessP95 = latency?.firstAudio.p95 ?? null;
  const mappedHealth = latency?.responsivenessHealth;
  const responsivenessHealth = mappedHealth === 'good'
    ? 'Fast'
    : mappedHealth === 'ok'
      ? 'Normal'
      : mappedHealth === 'slow'
        ? 'Slow'
        : !hasUsefulLatency || responsivenessP95 === null
          ? 'Warming Up'
          : responsivenessP95 < 1200
            ? 'Fast'
            : responsivenessP95 < 2200
              ? 'Normal'
              : 'Slow';
  const responsivenessColor = responsivenessHealth === 'Fast'
    ? 'emerald'
    : responsivenessHealth === 'Normal'
      ? 'amber'
      : responsivenessHealth === 'Slow'
        ? 'rose'
        : 'purple';

  const formatMs = (value: number | null | undefined) => {
    if (value === null || value === undefined) {
      return '--';
    }

    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}s`;
    }

    return `${Math.round(value)} ms`;
  };

  return (
    <div className="space-y-4">
      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-[#1a1a2e] border border-red-500/30 rounded-2xl p-8 max-w-md w-full shadow-2xl">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-xl bg-red-500/10 flex items-center justify-center border border-red-500/20">
                <svg className="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h2 className="text-white font-bold text-xl leading-none">Intelligence Error</h2>
                <p className="text-red-400 text-[10px] font-black uppercase tracking-widest mt-1">Failed to fetch metrics</p>
              </div>
            </div>

            <div className="bg-black/20 border border-white/5 rounded-xl p-4 mb-6">
              <p className="text-gray-400 text-xs font-mono leading-relaxed italic">
                {errorMessage}
              </p>
            </div>

            <button
              type="button"
              onClick={() => {
                setShowErrorModal(false);
              }}
              className="w-full px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-white text-[10px] font-black uppercase tracking-widest transition-colors border border-white/10"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
      {/* Clear Analytics Confirmation Modal */}
      {showClearConfirm && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-[100] p-4">
          <div className="bg-[#0a0a14] border border-red-500/30 rounded-[2.5rem] p-10 max-w-md w-full shadow-[0_0_50px_rgba(239,68,68,0.1)] relative overflow-hidden">
            {/* Red Background Accent */}
            <div className="absolute -top-24 -right-24 w-48 h-48 bg-red-500/10 blur-[80px] rounded-full" />

            <div className="relative z-10">
              <div className="flex items-center gap-5 mb-8">
                <div className="w-16 h-16 rounded-2xl bg-red-500/10 flex items-center justify-center shrink-0 border border-red-500/20">
                  <span className="text-3xl">⚠️</span>
                </div>
                <div>
                  <h2 className="text-white font-black text-xl uppercase tracking-tight">Clear Intelligence</h2>
                  <p className="text-red-400 text-[10px] font-black uppercase tracking-widest mt-0.5">Destroy analytics history</p>
                </div>
              </div>

              <p className="text-gray-400 text-[13px] mb-8 leading-relaxed">
                This will <span className="text-white font-bold underline decoration-red-500/50">permanently delete</span> all session metrics, message logs, and achievement tracking. The AI will lose its context for your past performance.
              </p>

              <div className="flex flex-col gap-3">
                <button
                  type="button"
                  onClick={() => { void handleClearAnalytics(); }}
                  className="w-full px-6 py-4 rounded-2xl bg-red-600 hover:bg-red-500 text-white text-sm font-black uppercase tracking-widest transition-all shadow-[0_10px_30px_rgba(220,38,38,0.3)] hover:scale-[1.02] active:scale-[0.98]"
                >
                  Confirm Deletion
                </button>
                <button
                  type="button"
                  onClick={() => setShowClearConfirm(false)}
                  className="w-full px-6 py-4 rounded-2xl bg-white/5 hover:bg-white/10 text-gray-500 hover:text-white text-xs font-black uppercase tracking-widest transition-all"
                >
                  Keep Data
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-3 xl:flex-row xl:items-stretch xl:justify-between">
        {/* Header Card */}
        <div className="relative flex-1 overflow-hidden rounded-[24px] border border-white/10 bg-[linear-gradient(135deg,rgba(59,130,246,0.13),rgba(168,85,247,0.12),rgba(255,255,255,0.025))] p-5 shadow-[0_20px_60px_rgba(7,12,28,0.3)]">
          <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-fuchsia-400/10 blur-3xl" />
          <div className="absolute left-10 right-16 top-0 h-px bg-gradient-to-r from-transparent via-cyan-300/50 to-transparent" />
          <div className="relative max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.05] px-3 py-1 text-[10px] font-black uppercase tracking-[0.24em] text-cyan-200/80">
              Analytics
            </div>
            <h3 className="mt-3 text-3xl font-black tracking-[-0.04em] text-white">Stream performance</h3>
            <p className="mt-2 text-sm leading-6 text-slate-300/72">
              See how often StreamBuddy is active, how much chat it processes, and whether response timing feels ready for live use.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 self-start rounded-[24px] border border-white/10 bg-black/24 p-2 xl:self-stretch">
          <div className="flex gap-1.5 rounded-[18px] border border-white/10 bg-black/30 p-1.5">
            {(['7d', '30d', '90d'] as const).map((range) => (
              <button
                type="button"
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-4 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-[0.24em] transition-all ${timeRange === range
                  ? 'bg-[linear-gradient(135deg,#9333ea,#0891b2)] text-white shadow-[0_12px_28px_rgba(8,145,178,0.28)]'
                  : 'text-white/40 hover:text-white/70'
                  }`}
              >
                {range.toUpperCase()}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={() => setShowClearConfirm(true)}
            className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-black/20 text-white/35 transition-all hover:border-red-400/30 hover:text-red-300"
            title="Clear Analytics"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>

      {inlineNotice && (
        <div className="rounded-2xl border border-amber-500/25 bg-amber-500/10 px-4 py-3">
          <p className="text-[11px] text-amber-300">{inlineNotice}</p>
        </div>
      )}

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard title="Total Sessions" value={data?.stats?.totalSessions ?? 0} subtitle="Unique Streams" icon="🎲" color="purple" />
        <StatCard title="Stream Time" value={formatDuration(data?.stats?.totalStreamTime ?? 0)} subtitle="Total Hours" icon="⏳" color="blue" />
        <StatCard title="Chat Analyzed" value={formatNumber(data?.stats?.totalMessagesAnalyzed ?? 0)} subtitle="Total Messages" icon="💬" color="emerald" />
        <StatCard title="AI Activity" value={formatDuration((data?.stats?.totalAiMinutes ?? 0) * 60)} subtitle="TTS Active Time" icon="🧠" color="amber" />
      </div>

      {/* Secondary Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard title="Lore Triggered" value={data?.stats?.totalAlertsTriggered ?? 0} subtitle="Events Context" icon="✨" color="emerald" />
        <StatCard title="Donations Read" value={data?.stats?.totalDonationsRead ?? 0} subtitle="Bits/Subs Sync" icon="💎" color="rose" />
        <StatCard title="Avg Duration" value={formatDuration(data?.stats?.averageSessionDuration ?? 0)} subtitle="Per Session" icon="📏" color="blue" />
        <StatCard title="Chat Density" value={formatNumber(Math.round(data?.stats?.averageMessagesPerSession ?? 0))} subtitle="Avg Msg/Session" icon="📊" color="purple" />
      </div>

      {/* Responsiveness */}
      <div className="rounded-[24px] border border-white/10 bg-[linear-gradient(145deg,rgba(255,255,255,0.045),rgba(0,0,0,0.18))] overflow-hidden shadow-2xl p-5 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-[10px] font-black text-white uppercase tracking-[0.2em] opacity-60">Voice Responsiveness</h3>
            <p className="text-gray-500 text-[11px] mt-1">How fast the assistant starts speaking after you talk. Lower is better.</p>
            <p className="text-[11px] text-gray-300 mt-2">
              Typical reply delay: <span className="text-white font-bold">{formatMs(latency?.typicalReplyDelayMs ?? latency?.firstAudio.p50)}</span>
            </p>
          </div>
          <span className={`px-3 py-1 rounded-xl text-[10px] font-black uppercase tracking-widest border ${responsivenessColor === 'emerald'
            ? 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10'
            : responsivenessColor === 'amber'
              ? 'text-amber-400 border-amber-500/30 bg-amber-500/10'
              : responsivenessColor === 'rose'
                ? 'text-rose-400 border-rose-500/30 bg-rose-500/10'
                : 'text-purple-400 border-purple-500/30 bg-purple-500/10'
            }`}
          >
            {responsivenessHealth}
          </span>
        </div>

        {!hasUsefulLatency ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-[11px] text-gray-400">
            Still learning your baseline. We need about 20 responses before these speed numbers become reliable.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <StatCard title="Usually Starts In" value={formatMs(latency?.firstAudio.p50)} subtitle="Typical response start" icon="⚡" color="blue" />
            <StatCard title="Slow Moments" value={formatMs(latency?.firstAudio.p95)} subtitle="Slower response starts" icon="🎯" color="amber" />
            <StatCard title="Typical Full Reply" value={formatMs(latency?.turnDuration.p50)} subtitle="From start to finish" icon="⏱️" color="emerald" />
          </div>
        )}

        <button
          type="button"
          onClick={() => setShowAdvancedLatency(prev => !prev)}
          className="px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-[10px] font-black uppercase tracking-widest text-gray-300"
        >
          {showAdvancedLatency ? 'Hide Technical Details' : 'Show Technical Details'}
        </button>

        {showAdvancedLatency && latency && (
          <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
            <p className="text-[10px] text-gray-500 uppercase tracking-widest mb-3">For debugging: percentile metrics</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-[11px]">
              <div className="text-gray-400">Response start (p50): <span className="text-white font-bold">{formatMs(latency.firstAudio.p50)}</span></div>
              <div className="text-gray-400">Response start (p95): <span className="text-white font-bold">{formatMs(latency.firstAudio.p95)}</span></div>
              <div className="text-gray-400">Response start (p99): <span className="text-white font-bold">{formatMs(latency.firstAudio.p99)}</span></div>
              <div className="text-gray-400">Full reply time (p50): <span className="text-white font-bold">{formatMs(latency.turnDuration.p50)}</span></div>
              <div className="text-gray-400">Full reply time (p95): <span className="text-white font-bold">{formatMs(latency.turnDuration.p95)}</span></div>
              <div className="text-gray-400">Full reply time (p99): <span className="text-white font-bold">{formatMs(latency.turnDuration.p99)}</span></div>
              <div className="text-gray-400">First-audio samples: <span className="text-white font-bold">{latency.firstAudioSamples}</span></div>
              <div className="text-gray-400">Turn-duration samples: <span className="text-white font-bold">{latency.turnDurationSamples}</span></div>
              <div className="text-gray-400">Last updated: <span className="text-white font-bold">{latency.lastUpdatedAt ? new Date(latency.lastUpdatedAt).toLocaleTimeString() : '--'}</span></div>
            </div>
          </div>
        )}
      </div>

      {/* Session List */}
      <div className="rounded-[24px] border border-white/10 bg-black/20 overflow-hidden shadow-2xl">
        <div className="px-5 py-4 border-b border-white/8 flex items-center justify-between bg-white/[0.025]">
          <h3 className="text-[10px] font-black text-white uppercase tracking-[0.2em] opacity-50">Recent Sessions</h3>
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
            <span className="text-[8px] font-black text-blue-400 uppercase tracking-widest">History Log</span>
          </div>
        </div>

        <div className="overflow-x-auto">
          {(!data || data.recentSessions.length === 0) ? (
            <div className="p-16 text-center">
              <div className="text-4xl mb-4 opacity-10">🌑</div>
              <p className="text-gray-500 font-black text-[10px] uppercase tracking-widest">No Active Session Data Detected</p>
              <p className="text-gray-600 text-[10px] mt-1">Start streaming to populate the performance records.</p>
            </div>
          ) : (
            <table className="w-full text-left">
              <thead className="text-[9px] font-black text-gray-500 uppercase tracking-widest bg-black/40">
                <tr>
                  <th className="px-8 py-4">Timestamp</th>
                  <th className="px-8 py-4">Runtime</th>
                  <th className="px-8 py-4">Load</th>
                  <th className="px-8 py-4">Activity</th>
                  <th className="px-8 py-4">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {data.recentSessions.map((session) => {
                  const timestamp = formatSessionTimestamp(session.startedAt);

                  return (
                  <tr key={session.id} className="hover:bg-white/[0.03] transition-all group">
                    <td className="px-8 py-5">
                      <div className="flex flex-col">
                        <span className="text-white text-xs font-bold font-mono">
                          {timestamp.date}
                        </span>
                        <span className="text-[10px] text-gray-500 font-mono">
                          {timestamp.time}
                        </span>
                      </div>
                    </td>
                    <td className="px-8 py-5">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-300 text-xs font-mono">{formatDuration(session.duration)}</span>
                      </div>
                    </td>
                    <td className="px-8 py-5">
                      <div className="flex flex-col">
                        <span className="text-gray-300 text-xs font-mono">{formatNumber(session.messagesAnalyzed)} <span className="text-[9px] opacity-40 uppercase">msg</span></span>
                        <div className="w-16 h-1 rounded-full bg-white/5 mt-1 overflow-hidden">
                          <div className="h-full bg-emerald-500/40" style={{ width: `${Math.min(100, (session.messagesAnalyzed / 1000) * 100)}%` }} />
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-5">
                      <div className="flex items-center gap-1.5">
                        <span className="text-blue-400 text-xs font-mono">{formatDuration(session.aiSecondsUsed)}</span>
                        <span className="text-[9px] text-gray-600 font-black uppercase tracking-tighter">AI-Active</span>
                      </div>
                    </td>
                    <td className="px-8 py-5">
                      {session.endedAt ? (
                        <div className="flex items-center gap-1.5 opacity-50">
                          <div className="w-1 h-1 rounded-full bg-gray-500" />
                          <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Archived</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5">
                          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.5)]" />
                          <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest">Active Live</span>
                        </div>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default AnalyticsDashboard;
